<script setup lang="ts">
/**
 * UI-kit component
 * Category: Fields
 * Name: SelectSearch
 */
import VueSelect from 'vue-select'

import type { SelectSearchOption, SelectSearchProps } from '../types/interfaces'

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { AppText } from '@shared/ui/typography'
import { IconContainer } from '@shared/ui/other'

import { useSelectSearch } from '../composables/use-select-search.hook'

defineOptions({
  name: 'SelectSearch',
})

const props = withDefaults(defineProps<SelectSearchProps>(), {
  noOptions: '',
  label: '',
  disabled: false,
  clearable: true,
  required: false,
  className: '',
  placeholder: 'Type here',
  placement: 'bottom',
  isAvailablePosition: false,
  mode: 'normal',
})

const emit = defineEmits<{
  change: [SelectSearchOption]
}>()

const {
  refValue,
  dropdownStyle,
  handleSelect,
  handleOpen,
} = useSelectSearch({
  props,
  onChange: (option: SelectSearchOption) => emit('change', option),
})
</script>

<template>
  <div
    ref="selectSearch"
    :class="['ui-select-search', className, `options-${placement}`, { 'is-available-position': isAvailablePosition }, mode]"
    :style="dropdownStyle"
  >
    <AppText
      v-if="label"
      name="body2"
      :for="id"
      class="ui-select-search__label"
    >
      {{ label }}
      <span
        v-if="required"
        class="ui-select-search__required"
      >*</span>
    </AppText>
    <VueSelect
      v-model="refValue"
      :options="options"
      :disabled="disabled"
      :clearable="clearable"
      label="text"
      :placeholder="placeholder"
      @option:selected="handleSelect"
      @open="handleOpen"
    >
      <template #no-options>
        <span class="vs__no-options">{{ noOptions }}</span>
      </template>
      <template #open-indicator>
        <IconContainer class="ui-select-search__arrow vs__arrow">
          <FontAwesomeIcon :icon="faChevronDown" />
        </IconContainer>
      </template>
      <template #selected-option-container="{ option }">
        <IconContainer
          v-if="option.icon"
          :class="option.colorIcon"
        >
          <FontAwesomeIcon :icon="option.icon" />
        </IconContainer>
        <AppText
          name="body2"
          :class="option.colorText"
        >
          {{ option.text }}
        </AppText>
      </template>
      <template #option="option">
        <IconContainer
          v-if="option.icon"
          :class="option.colorIcon"
        >
          <FontAwesomeIcon :icon="option.icon" />
        </IconContainer>
        <AppText
          name="body2"
          :class="option.colorText"
        >
          {{ option.text }}
        </AppText>
      </template>
      <template #search="{ attributes, events }">
        <input
          class="vs__search"
          v-bind="attributes"
          v-on="events"
        >
      </template>
    </VueSelect>
  </div>
</template>

<style lang="scss" scoped src="../styles/_select-search.scss" />
